import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { difference, isEmpty, mapValues } from 'lodash';
import queryString from 'query-string';

import { useSearchContext } from '@app/contexts';
import { useLocationsFetch } from '@app/hooks';
import { makeRequest } from '@app/utils';

import { getCQLState } from '../../utils';
import { getInitialFacets, getMappedFacetValues } from './utils';
import {
  FACET_LIMIT,
  FacetInstanceDto,
  FacetsDto,
  UnmappedFacetValue,
} from './constants';

interface FacetRequestProps {
  isStatePristine: boolean;
  expandedFacets?: string[];
}

export const useFacetsRequest = ({
  isStatePristine,
  expandedFacets = [],
}: FacetRequestProps): FacetInstanceDto => {
  const { $t } = useIntl();
  const [facetGroups, setFacetGroups] = useState(getInitialFacets($t));
  const [expandedFacetsQuery, setExpandedFacetsQuery] = useState('');
  const locationsQuery = useLocationsFetch();
  const {
    state: { option, query, publicationYear, facets: selectedFacets },
  } = useSearchContext();

  useEffect(() => {
    if (expandedFacets.length) {
      setExpandedFacetsQuery(
        expandedFacets.map(facet => `${facet}:${FACET_LIMIT}`).join(',')
      );
    }
  }, [expandedFacets]);

  const shouldGetFacets =
    (!isStatePristine || !isEmpty(selectedFacets)) &&
    Boolean(expandedFacetsQuery);

  const actualFacetsQuery = useQuery(
    [
      'facets',
      expandedFacetsQuery,
      selectedFacets,
      option,
      query,
      publicationYear,
    ],
    () =>
      makeRequest<FacetsDto>({
        url: '/search/instances/facets',
        params: {
          facet: expandedFacetsQuery,
          ...getCQLState({
            query,
            facets: selectedFacets,
            option,
            publicationYear,
            hasRequestedFacets: Boolean(expandedFacetsQuery),
          }),
        },
        paramsSerializer: params => queryString.stringify(params),
      }),
    {
      enabled: shouldGetFacets,
    }
  );

  const getNullishFacets = (facetsForKey, facetKey): UnmappedFacetValue[] => {
    if (!selectedFacets) {
      return [];
    }

    return difference(
      selectedFacets[facetKey],
      facetsForKey?.map(
        facetValue => selectedFacets[facetKey]?.find(el => el === facetValue.id)
      )
    ).map((facetValue: string) => ({ id: facetValue, totalRecords: 0 }));
  };

  useEffect(() => {
    if (actualFacetsQuery.isError) {
      setFacetGroups(
        previousFacetGroups =>
          mapValues(previousFacetGroups, (value, key) => {
            const isFacedExpanded = expandedFacets?.includes(key);
            const isFacedNullish = value.values === null;

            return {
              ...value,
              isError: isFacedExpanded ? isFacedNullish : false,
            };
          }) as FacetInstanceDto
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFacetsQuery.isError]);

  useEffect(() => {
    if (actualFacetsQuery.data) {
      const updatedFacets = expandedFacets.reduce(
        (newFacets, facetKey) => {
          const facetValues =
            actualFacetsQuery.data.facets[facetKey]?.values || [];
          const nullishFacets = getNullishFacets(facetValues, facetKey);
          const mappedValues = getMappedFacetValues({
            $t,
            facetName: facetKey,
            values: [...nullishFacets, ...facetValues],
            locations: locationsQuery.data,
          });

          newFacets[facetKey] = {
            ...newFacets[facetKey],
            values: mappedValues,
            isError: false,
          };

          return newFacets;
        },
        { ...facetGroups }
      );

      setFacetGroups(updatedFacets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFacetsQuery.data]);

  return facetGroups;
};
